import { useMemo, useState } from "react";
import { CSHForm } from "../../components/forms/CSHForm";
import type { CSHFormData } from "../../components/forms/CSHForm";
import Modal from "../../components/ui/Modal";
import type { AssignedUserSelection, CshGroup } from "../../services/csh/csh.types";
import { getAssignedUserId, mapGroupToCshForm, validateCshForm } from "./cshGroupAssignments";
import type { CshCustomerOption, CshFormErrors } from "./cshGroupAssignments";

interface Props {
    group: CshGroup | null;
    users: AssignedUserSelection[];
    customers: CshCustomerOption[];
    submitting: boolean;
    error: string | null;
    onClearError: () => void;
    onSave: (data: CSHFormData) => Promise<void>;
    onCancel: () => void;
}

export function CshGroupEditor({ group, users, customers, submitting, error, onClearError, onSave, onCancel }: Props) {
    const [formData, setFormData] = useState(() => mapGroupToCshForm(group));
    const [errors, setErrors] = useState<CshFormErrors>({});
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const formUsers = useMemo(() => users.flatMap(user => {
        const id = user.id ?? user.userId;
        return id == null ? [] : [{
            id, firstName: user.firstName ?? "", lastName: user.lastName ?? "", employeeId: user.employeeId ?? "",
        }];
    }), [users]);
    const selectedCustomers = useMemo(() => group ? group.customers.map(customer => ({
        customerId: String(customer.customerId),
        selectedSites: customer.sites.map(site => String(site.siteId)),
        siteOptions: customer.sites.map(site => ({
            id: site.siteId, siteName: site.siteName, displayName: site.siteName ?? `Site ${site.siteId}`,
        })),
    })) : null, [group]);
    const selectedUsers = useMemo(() => group ? group.assignedTo.map(getAssignedUserId) : null, [group]);

    const submit = async () => {
        setHasSubmitted(true);
        onClearError();
        const errors = validateCshForm(formData);
        setErrors(errors);
        if (Object.keys(errors).length) return;
        await onSave(formData);
    };

    return (
        <Modal isOpen title={group ? "Edit CSH Group" : "Add CSH Group"}
            okButtonLoading={submitting} cancelButtonLoading={submitting}
            content={
                <CSHForm
                    mode={group ? "edit" : "create"}
                    formData={formData}
                    setFormData={setFormData}
                    errors={errors}
                    validateField={data => setErrors(validateCshForm(data))}
                    hasSubmitted={hasSubmitted}
                    serverError={error}
                    onClearServerError={onClearError}
                    usersData={formUsers}
                    customerData={customers}
                    selectedCustomers={selectedCustomers}
                    selectedAssignedTo={selectedUsers}
                />
            }
            onConfirm={submit}
            onCancel={() => { if (!submitting) onCancel(); }}
        />
    );
}
